import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { BRAND_ORANGE } from '../../../../styles/portalTheme';

const UPCOMING_GRAY = '#9E9E9E';
const CONNECTOR_GRAY = '#E0E0E0';

const StepperBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  padding: theme.spacing(1.75, 1),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
}));

const StepColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const IconRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginBottom: 8,
});

const StepIconOuter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'upcoming' && prop !== 'current',
})(({ upcoming, current }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: upcoming ? UPCOMING_GRAY : BRAND_ORANGE,
  boxShadow: current ? '0 0 0 3px rgba(224, 104, 10, 0.18)' : 'none',
  zIndex: 1,
}));

const Connector = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'hidden',
})(({ active, hidden }) => ({
  flex: 1,
  height: 2,
  minWidth: 4,
  backgroundColor: hidden ? 'transparent' : active ? BRAND_ORANGE : CONNECTOR_GRAY,
}));

const StepIcon = ({ completed, upcoming, current, stepNumber }) => {
  if (completed) {
    return (
      <StepIconOuter>
        <TaskAltIcon sx={{ fontSize: 18, color: 'white' }} />
      </StepIconOuter>
    );
  }

  return (
    <StepIconOuter upcoming={upcoming} current={current}>
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 700,
          color: '#fff',
          lineHeight: 1,
        }}
      >
        {stepNumber}
      </Typography>
    </StepIconOuter>
  );
};

const ManageEventStepper = ({ activeStep, steps }) => (
  <StepperBar>
    {steps.map((step, index) => {
      const isCompleted = activeStep > index;
      const isUpcoming = activeStep < index;
      const isCurrent = activeStep === index;

      return (
        <StepColumn key={step.id}>
          <IconRow>
            <Connector hidden={index === 0} active={activeStep >= index} />
            <StepIcon
              completed={isCompleted}
              upcoming={isUpcoming}
              current={isCurrent}
              stepNumber={index + 1}
            />
            <Connector hidden={index === steps.length - 1} active={activeStep > index} />
          </IconRow>
          <Typography
            aria-current={isCurrent ? 'step' : undefined}
            sx={{
              textAlign: 'center',
              fontSize: { xs: '0.625rem', sm: '0.75rem' },
              lineHeight: 1.25,
              fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
              color: isUpcoming ? UPCOMING_GRAY : isCurrent ? BRAND_ORANGE : '#374151',
              px: 0.5,
              maxWidth: '100%',
            }}
          >
            {step.label}
          </Typography>
        </StepColumn>
      );
    })}
  </StepperBar>
);

export default ManageEventStepper;
